import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

function AssignmentUploadModal({ classId, classGradeLevel, discipline = 'stem', onClose, onUploadSuccess }) {
  const [assignmentName, setAssignmentName] = useState('');
  const [jsonInput, setJsonInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadMode, setUploadMode] = useState('paste'); // 'paste' or 'file'
  const [reach, setReach] = useState('Partial grade');
  const [step, setStep] = useState('upload'); // 'upload' or 'review'
  const [parsedEvidences, setParsedEvidences] = useState([]);
  const [allObjectives, setAllObjectives] = useState([]);

  // Load objectives on mount
  useEffect(() => {
    const loadObjectives = async () => {
      try {
        console.log('AssignmentUploadModal - Loading objectives for discipline:', discipline);
        const response = await axios.get('/outcomes/api/objectives-hierarchy', {
          params: { discipline },
          withCredentials: true
        });
        console.log('AssignmentUploadModal - Objectives response:', response.data);
        
        // Flatten all objectives from the hierarchy
        const objectives = [];
        response.data.forEach(gradeband => {
          if (gradeband.outcomes) {
            gradeband.outcomes.forEach(outcome => {
              if (outcome.objectives) {
                outcome.objectives.forEach(obj => {
                  objectives.push({
                    id: obj.id,
                    number: obj.number,
                    text: obj.text,
                    outcome_id: outcome.id,
                    gradeband_id: gradeband.id,
                    gradeband_title: gradeband.title
                  });
                });
              }
            });
          }
        });
        console.log('AssignmentUploadModal - Flattened objectives:', objectives);
        setAllObjectives(objectives);
      } catch (err) {
        console.error('AssignmentUploadModal - Failed to load objectives:', err);
      }
    };
    loadObjectives();
  }, [discipline]);

  const normalizeLevel = (level) => {
    if (!level) return 'Explore';
    const normalized = level.toLowerCase().trim();
    
    if (normalized === 'explore') return 'Explore';
    if (normalized === 'apply') return 'Apply';
    if (normalized === 'independently implement') return 'Independently Implement';
    
    // Default to Explore if unknown value
    return 'Explore';
  };

  const stripCitations = (text) => {
    if (!text) return text;
    return text
      .replace(/\[cite_start\]/g, '')
      .replace(/\[cite:\s*\d+[\d,\s]*\]/g, '')
      .trim();
  };

  const parseJsonFile = (content) => {
    try {
      let cleanedContent = content
        .replace(/\[cite_start\]/g, '')
        .replace(/\[cite:\s*[\d,\s]*\]/g, '')
        .replace(/,(\s*[}\]])/g, '$1');
      
      const data = JSON.parse(cleanedContent);
      if (!data.evidences || !Array.isArray(data.evidences)) {
        setError(`Invalid JSON format. Expected { "evidences": [...] }. Got: ${JSON.stringify(data).substring(0, 100)}`);
        return null;
      }
      return data;
    } catch (err) {
      setError(`JSON Parse Error: ${err.message}`);
      return null;
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result;
      if (typeof content === 'string') {
        setJsonInput(content);
        setError('');
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  };

  const handleProceedToReview = (e) => {
    e.preventDefault();
    setError('');

    if (!assignmentName.trim()) {
      setError('Assignment name is required');
      return;
    }

    if (!jsonInput.trim()) {
      setError('Please provide JSON data');
      return;
    }

    const parsed = parseJsonFile(jsonInput);
    if (!parsed) return;

    // Process evidences for review
    const evidences = parsed.evidences.map((ev, idx) => ({
      id: idx,
      objective_id: ev._db_id || null,
      level: normalizeLevel(ev.level),
      description: stripCitations(ev.rationale || ''),
      _originalDb: ev._db_id
    }));

    if (evidences.length === 0) {
      setError('No evidences found in the JSON');
      return;
    }

    setParsedEvidences(evidences);
    setStep('review');
  };

  const updateEvidence = (id, field, value) => {
    setParsedEvidences(evidences =>
      evidences.map(ev =>
        ev.id === id ? { ...ev, [field]: value } : ev
      )
    );
  };

  const deleteEvidence = (id) => {
    setParsedEvidences(evidences => evidences.filter(ev => ev.id !== id));
  };

  const handleImport = async (e) => {
    e.preventDefault();
    setError('');

    const validEvidences = parsedEvidences.filter(ev => ev.objective_id);
    if (validEvidences.length === 0) {
      setError('Please select an objective for at least one evidence');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        assignmentName: assignmentName.trim(),
        reach: reach,
        discipline: discipline,
        evidences: validEvidences.map((ev) => ({
          _db_id: ev.objective_id,
          title: assignmentName.trim(),
          level: ev.level,
          rationale: ev.description,
        })),
      };

      const response = await axios.post('/outcomes/api/bulk-evidence', payload, {
        params: { classId },
      });

      if (response.data.success) {
        alert(
          `Successfully imported ${response.data.count} evidence${
            response.data.count !== 1 ? 's' : ''
          }`
        );
        onUploadSuccess();
        onClose();
      } else {
        setError(response.data.error || 'Upload failed');
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || 'Failed to upload evidences'
      );
      console.error('Upload error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'review') {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-review" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Review & Match Evidences</h3>
            <button className="close-btn" onClick={onClose}>
              ✕
            </button>
          </div>

          <form onSubmit={handleImport} className="evidence-review-form">
            <div className="review-intro">
              <p>Review the evidences below. Select the objective for each evidence, edit the description if needed, and choose the achievement level.</p>
              {allObjectives.length === 0 && (
                <div className="warning-message">⚠️ No objectives loaded. Make sure the discipline has objectives configured.</div>
              )}
              {parsedEvidences.length === 0 && (
                <div className="error-message">No evidences to import</div>
              )}
            </div>

            {parsedEvidences.length > 0 && (
              <div className="evidences-review-list">
                {parsedEvidences.map((evidence) => (
                  <div key={evidence.id} className="evidence-review-card">
                    <div className="evidence-review-header">
                      <h4>Evidence #{evidence.id + 1}</h4>
                      <button
                        type="button"
                        className="delete-evidence-btn"
                        onClick={() => deleteEvidence(evidence.id)}
                        title="Delete this evidence"
                      >
                        🗑️
                      </button>
                    </div>

                    <div className="form-group">
                      <label>Objective *</label>
                      <select
                        value={evidence.objective_id || ''}
                        onChange={(e) => updateEvidence(evidence.id, 'objective_id', e.target.value ? parseInt(e.target.value) : null)}
                        className={evidence.objective_id ? '' : 'empty-select'}
                      >
                        <option value="">-- Select an objective --</option>
                        {allObjectives.map((obj) => (
                          <option key={obj.id} value={obj.id}>
                            {obj.gradeband_title} - O{obj.number}: {obj.text.substring(0, 60)}...
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Achievement Level *</label>
                      <select
                        value={evidence.level}
                        onChange={(e) => updateEvidence(evidence.id, 'level', e.target.value)}
                      >
                        <option value="Explore">Explore</option>
                        <option value="Apply">Apply</option>
                        <option value="Independently Implement">Independently Implement</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Description / Evidence</label>
                      <textarea
                        value={evidence.description}
                        onChange={(e) => updateEvidence(evidence.id, 'description', e.target.value)}
                        rows={3}
                        placeholder="Enter evidence details..."
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {error && <div className="error-message">{error}</div>}

            <div className="form-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setStep('upload')}
                disabled={isLoading}
              >
                Back
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading || parsedEvidences.length === 0}
              >
                {isLoading ? 'Importing...' : `Import ${parsedEvidences.filter(e => e.objective_id).length} Evidence(s)`}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Import Assignment Evidences</h3>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleProceedToReview} className="assignment-upload-form">
          <div className="form-group">
            <label htmlFor="assignmentName">Assignment Name *</label>
            <input
              id="assignmentName"
              type="text"
              value={assignmentName}
              onChange={(e) => setAssignmentName(e.target.value)}
              placeholder="e.g., Rocket Design Project"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="reach">Reach *</label>
            <select
              id="reach"
              value={reach}
              onChange={(e) => setReach(e.target.value)}
              disabled={isLoading}
            >
              <option value="Partial grade">Partial grade</option>
              <option value="Whole grade">Whole grade</option>
            </select>
          </div>

          <div className="form-group">
            <label>JSON Format *</label>
            <div className="mode-selector">
              <button
                type="button"
                className={`mode-btn ${uploadMode === 'paste' ? 'active' : ''}`}
                onClick={() => setUploadMode('paste')}
                disabled={isLoading}
              >
                Paste JSON
              </button>
              <button
                type="button"
                className={`mode-btn ${uploadMode === 'file' ? 'active' : ''}`}
                onClick={() => setUploadMode('file')}
                disabled={isLoading}
              >
                Upload File
              </button>
            </div>
          </div>

          {uploadMode === 'paste' ? (
            <div className="form-group">
              <label htmlFor="jsonInput">JSON Data</label>
              <textarea
                id="jsonInput"
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder={`{
  "evidences": [
    {
      "_db_id": 21,
      "level": "apply",
      "rationale": "Students demonstrated..."
    }
  ]
}`}
                rows={10}
                disabled={isLoading}
              />
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="fileInput">Select JSON File</label>
              <input
                id="fileInput"
                type="file"
                accept=".json"
                onChange={handleFileUpload}
                disabled={isLoading}
              />
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              Next: Review Evidences
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


export default AssignmentUploadModal;
